import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Sparkles, BookOpen, Target, CheckCircle2, Wallet, ListChecks, Bot, Building2,
  Lightbulb, Briefcase, TrendingUp, ShieldCheck, Network, Quote, Loader2,
  RotateCcw, ChevronRight, AlertTriangle, ExternalLink,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PreviewUnlockOverlay from '../components/PreviewUnlockOverlay';
import { normalizeEntrepreneurship } from '../lib/entrepreneurshipNormalizer';
import { loadContent } from '../lib/contentLoader';
import {
  RolePreviewLayout,
  ScrollSection,
  SectionHeader,
  RatingCard,
  BulletList,
  Pills,
} from '../components/RolePreviewLayout';
import { Button } from '../components/ui/button';

const SECTIONS = [
  { id: 'snapshot', label: 'Snapshot', icon: Sparkles },
  { id: 'overview', label: 'Overview', icon: BookOpen },
  { id: 'quiz', label: 'Self-Check Quiz', icon: Target },
  { id: 'who', label: 'Who Should Choose', icon: CheckCircle2 },
  { id: 'costing', label: 'Costing', icon: Wallet },
  { id: 'process', label: 'Process', icon: ListChecks },
  { id: 'tools', label: 'Tools & AI', icon: Bot },
  { id: 'govt', label: 'Government Support', icon: Building2 },
  { id: 'projects', label: 'Projects You Can Build', icon: Lightbulb },
  { id: 'career', label: 'Career & Industry', icon: Briefcase },
  { id: 'future', label: 'Future Scope', icon: TrendingUp },
  { id: 'myths', label: 'Myths vs Reality', icon: ShieldCheck },
  { id: 'related', label: 'Related Pathways', icon: Network },
  { id: 'takeaway', label: 'Final Takeaway', icon: Quote },
];

const CATEGORY_ACCENT = {
  'Food':                  { from: 'from-orange-600', via: 'via-amber-700', to: 'to-red-700' },
  'Food/Health':           { from: 'from-rose-600', via: 'via-pink-700', to: 'to-red-800' },
  'Food Processing':       { from: 'from-amber-600', via: 'via-yellow-700', to: 'to-orange-800' },
  'Green Energy':          { from: 'from-emerald-600', via: 'via-green-700', to: 'to-teal-800' },
  'EV/Mobility':           { from: 'from-sky-600', via: 'via-cyan-700', to: 'to-blue-800' },
  'Digital':               { from: 'from-violet-600', via: 'via-purple-700', to: 'to-fuchsia-800' },
  'Services':              { from: 'from-pink-600', via: 'via-rose-700', to: 'to-red-800' },
  'Repair/Retail':         { from: 'from-cyan-600', via: 'via-teal-700', to: 'to-emerald-800' },
  'Services/Manufacturing':{ from: 'from-stone-600', via: 'via-zinc-800', to: 'to-slate-900' },
  'Education':             { from: 'from-indigo-600', via: 'via-violet-700', to: 'to-purple-800' },
  'Manufacturing':         { from: 'from-zinc-700', via: 'via-slate-800', to: 'to-gray-900' },
};
const getAccent = (c) => CATEGORY_ACCENT[c] || { from: 'from-emerald-600', via: 'via-teal-700', to: 'to-cyan-800' };

const difficultyScore = (d = '') => {
  const s = d.toLowerCase();
  if (s.includes('advanced')) return 5;
  if (s.includes('intermediate')) return 4;
  if (s.includes('beginner')) return 3;
  return 3;
};

const EntrepreneurshipDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadContent('entrepreneurship.json')
      .then((d) => {
        if (d && !d.__locked) setPkg(normalizeEntrepreneurship(d));
        setLoading(false);
      })
      .catch(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [id]);

  const p = useMemo(() => pkg?.pathways.find(x => x.id === id), [pkg, id]);
  const related = useMemo(() => {
    if (!pkg || !p) return [];
    return pkg.pathways.filter(x => x.category === p.category && x.id !== p.id).slice(0, 4);
  }, [pkg, p]);
  const linkedSchemes = useMemo(() => {
    if (!pkg || !p) return [];
    // New shape: idea.schemesToUse[] is an array of scheme IDs — match those exactly
    const wantedIds = p.schemesToUse || [];
    if (wantedIds.length === 0) return [];
    return pkg.schemes.filter((g) => wantedIds.includes(g.id)).slice(0, 5);
  }, [pkg, p]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-gray-50"><Loader2 className="animate-spin text-emerald-600" size={32} /></div>;
  if (!p) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 bg-gray-50">
        <p className="text-gray-700 font-semibold mb-2">Pathway not found</p>
        <Button asChild><Link to="/explore/entrepreneurship">Back to all pathways</Link></Button>
      </div>
    );
  }

  const sec = p.sections || {};
  const accent = getAccent(p.category);

  return (
    <PreviewUnlockOverlay
      pathKey="entrepreneurship"
      previewId={String(p.id)}
      itemName={p.title}
      itemType="idea"
    >
    <div className="min-h-screen bg-white" data-testid="entrepreneurship-detail-page">
      <Navbar />

      <RolePreviewLayout
        sections={SECTIONS}
        heroAccent={accent}
        heroBadges={[
          { text: p.emoji ? `${p.emoji} ${p.category}` : p.category, tone: 'solid' },
          { text: p.mode },
          { text: p.difficulty || 'Beginner-friendly' },
        ]}
        heroEyebrow="Entrepreneurship · AP + Telangana"
        title={p.title}
        description={sec.snapshot?.whatItIs || p.previewCard?.subtext}
        onBack={() => navigate('/explore/entrepreneurship')}
        backLabel="Back to all pathways"
      >
        {/* 1. Snapshot */}
        <ScrollSection id="snapshot">
          <SectionHeader icon={Sparkles} title="Snapshot" subtitle="At-a-glance ratings & who this fits" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <RatingCard label="Difficulty" value={difficultyScore(p.difficulty)} />
            <RatingCard label="Daily Demand" value={5} />
            <RatingCard label="Earning Potential" value={4} />
            <RatingCard label="Govt Support" value={(p.sections?.governmentSupport?.length || 0) > 0 ? 5 : 3} />
            <RatingCard label="AI Resistance" value={4} />
            <RatingCard label="Student-Friendly" value={5} />
          </div>
          {(p.bestFor || []).length > 0 && (
            <div className="mt-5 p-5 bg-emerald-50 border border-emerald-200 rounded-xl">
              <p className="text-[11px] font-bold text-emerald-700 uppercase tracking-wider mb-2">Best Suited For</p>
              <Pills items={p.bestFor} tone="emerald" />
            </div>
          )}
          <div className="grid sm:grid-cols-3 gap-3 mt-5">
            <KV k="Starting budget" v={sec.snapshot?.startingBudget || p.startBudget} />
            <KV k="Time to test" v={sec.snapshot?.timeToTest || '3-7 days'} />
            <KV k="Earning logic" v={sec.snapshot?.earningLogic || 'Margin × volume − fixed costs'} />
          </div>
        </ScrollSection>

        {/* 2. Overview */}
        <ScrollSection id="overview">
          <SectionHeader icon={BookOpen} title="Overview" subtitle="Why this works for students" />
          {sec.overview?.whyItWorks && (
            <p className="text-base md:text-lg text-gray-800 leading-[1.7] mb-5">{sec.overview.whyItWorks}</p>
          )}
          {sec.overview?.customerSegments?.length > 0 && (
            <div className="mb-5">
              <p className="text-xs font-bold text-gray-700 mb-2">Customer segments</p>
              <Pills items={sec.overview.customerSegments} tone="blue" />
            </div>
          )}
          {sec.overview?.pricingModel && (
            <div className="p-5 bg-amber-50 border-l-4 border-amber-400 rounded-r-xl">
              <p className="text-xs font-bold text-amber-800 uppercase tracking-wider mb-1.5">Pricing model</p>
              <p className="text-base text-amber-900 leading-relaxed">{sec.overview.pricingModel}</p>
            </div>
          )}
        </ScrollSection>

        {/* 3. Self-Check Quiz */}
        {sec.selfCheckQuiz?.length > 0 && (
          <ScrollSection id="quiz">
            <SectionHeader icon={Target} title="Self-Check Quiz" subtitle={`Quick check if ${p.title} actually fits you`} />
            <PathwayQuiz quiz={sec.selfCheckQuiz} pathwayName={p.title} />
          </ScrollSection>
        )}

        {/* 4. Who Should Choose */}
        <ScrollSection id="who">
          <SectionHeader icon={CheckCircle2} title="Who Should Choose This Pathway" />
          {(sec.whoShouldChoose || p.bestFor || []).length > 0 ? (
            <Pills items={sec.whoShouldChoose || p.bestFor} tone="emerald" />
          ) : (
            <p className="text-sm text-gray-500 italic">Open to any student who wants to test a real business idea.</p>
          )}
        </ScrollSection>

        {/* 5. Costing */}
        {sec.costing && (
          <ScrollSection id="costing">
            <SectionHeader icon={Wallet} title="Costing" subtitle="What money will actually be spent" />
            {sec.costing.starterCosts?.length > 0 && (
              <div className="mb-5">
                <p className="text-xs font-bold text-gray-700 mb-2">Starter costs (one-time)</p>
                <div className="grid sm:grid-cols-2 gap-2.5">
                  {sec.costing.starterCosts.map((c, i) => (
                    <div key={i} className="p-3 bg-white border border-gray-200 rounded-xl flex items-start justify-between gap-3">
                      <span className="text-sm text-gray-800 flex-1">{c.item}</span>
                      <span className="text-sm font-bold text-emerald-700 whitespace-nowrap">{c.cost}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {sec.costing.monthlyOrDailyCosts?.length > 0 && (
              <div className="mb-5">
                <p className="text-xs font-bold text-gray-700 mb-2">Recurring (monthly / daily)</p>
                <div className="grid sm:grid-cols-2 gap-2.5">
                  {sec.costing.monthlyOrDailyCosts.map((c, i) => (
                    <div key={i} className="p-3 bg-white border border-gray-200 rounded-xl flex items-start justify-between gap-3">
                      <span className="text-sm text-gray-800 flex-1">{c.item}</span>
                      <span className="text-sm font-bold text-blue-700 whitespace-nowrap">{c.cost}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {sec.costing.simpleFormula && (
              <div className="p-5 bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl mb-3">
                <p className="text-[11px] font-bold text-emerald-700 uppercase tracking-wider mb-1.5">Simple formula</p>
                <p className="font-mono text-sm text-gray-800 leading-relaxed">{sec.costing.simpleFormula}</p>
              </div>
            )}
            {sec.costing.examplePricing && (
              <p className="text-sm text-gray-700"><span className="font-semibold">Example pricing:</span> {sec.costing.examplePricing}</p>
            )}
            {sec.costing.profitReality && (
              <div className="mt-5">
                <p className="text-xs font-bold text-gray-700 mb-2">Profit reality (estimates, not guarantees)</p>
                <div className="grid sm:grid-cols-2 gap-2.5">
                  {[
                    ['Est. monthly revenue', sec.costing.profitReality.estMonthlyRevenue],
                    ['Est. monthly costs', sec.costing.profitReality.estMonthlyCosts],
                    ['Est. monthly profit', sec.costing.profitReality.estMonthlyProfit],
                    ['Typical break-even', sec.costing.profitReality.typicalBreakEven],
                  ].filter(([, v]) => v).map(([label, v], i) => (
                    <div key={i} className="p-3 bg-white border border-gray-200 rounded-xl flex items-start justify-between gap-3">
                      <span className="text-sm text-gray-800 flex-1">{label}</span>
                      <span className="text-sm font-bold text-emerald-700 whitespace-nowrap">{v}</span>
                    </div>
                  ))}
                </div>
                {sec.costing.profitReality.note && (
                  <p className="text-xs text-gray-500 italic mt-2">{sec.costing.profitReality.note}</p>
                )}
              </div>
            )}
          </ScrollSection>
        )}

        {/* 6. Process */}
        {sec.process?.length > 0 && (
          <ScrollSection id="process">
            <SectionHeader icon={ListChecks} title="Process" subtitle="Step-by-step launch playbook" />
            <ol className="space-y-3">
              {sec.process.map((step, i) => (
                <li key={i} className="flex items-start gap-3 p-4 bg-white border-2 border-gray-100 rounded-xl">
                  <span className="mt-0.5 inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold shrink-0">{i + 1}</span>
                  <span className="text-base text-gray-800 leading-relaxed">{step}</span>
                </li>
              ))}
            </ol>
          </ScrollSection>
        )}

        {/* 7. Tools & AI */}
        {sec.toolsAndAI?.length > 0 && (
          <ScrollSection id="tools">
            <SectionHeader icon={Bot} title="Tools & AI" subtitle="Where AI saves you time" />
            <Pills items={sec.toolsAndAI} tone="indigo" />
          </ScrollSection>
        )}

        {/* 8. Government Support */}
        <ScrollSection id="govt">
          <SectionHeader icon={Building2} title="Government Support" subtitle="Schemes that may apply" />
          {sec.governmentSupport?.length > 0 && (
            <div className="mb-4">
              <p className="text-xs font-bold text-gray-700 mb-2">Tagged for this pathway</p>
              <Pills items={sec.governmentSupport} tone="amber" />
            </div>
          )}
          {linkedSchemes.length > 0 && (
            <div className="grid sm:grid-cols-2 gap-3 mt-4">
              {linkedSchemes.map(s => (
                <Link key={s.id} to={`/explore/entrepreneurship/scheme/${s.id}`} className="group p-4 bg-white border-2 border-amber-100 hover:border-amber-400 rounded-2xl transition-all">
                  <div className="flex items-start gap-2.5 mb-2">
                    <span className="text-2xl">{s.emoji}</span>
                    <div className="flex-1">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700">{s.region}</p>
                      <p className="text-sm font-bold text-gray-900 leading-tight">{s.title}</p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-600 line-clamp-2">{s.snapshot}</p>
                  <p className="mt-2 text-[11px] font-semibold text-amber-700 inline-flex items-center gap-1">Read scheme details <ExternalLink size={11} /></p>
                </Link>
              ))}
            </div>
          )}
          {sec.governmentLinks?.length > 0 && (
            <div className="mt-5">
              <p className="text-[11px] font-bold tracking-widest text-amber-700 uppercase mb-3 flex items-center gap-1.5">
                <ExternalLink size={13} /> Official portals for this pathway
              </p>
              <div className="grid sm:grid-cols-2 gap-2.5">
                {sec.governmentLinks.map((lnk, i) => (
                  <a
                    key={i}
                    href={lnk.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`pathway-gov-link-${i}`}
                    className="group flex items-start gap-2.5 p-3 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-xl transition-colors"
                  >
                    <ExternalLink size={14} className="text-amber-700 mt-0.5 flex-shrink-0 group-hover:translate-x-0.5 transition-transform" />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 leading-snug">{lnk.label}</p>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700 mt-0.5">{lnk.type?.replace(/-/g, ' ')}</p>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}
          <div className="mt-5 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-r-xl flex gap-3">
            <AlertTriangle className="text-yellow-700 shrink-0 mt-0.5" size={16} />
            <p className="text-xs text-yellow-900 leading-relaxed">Subsidy amounts, eligibility, and deadlines must be verified on the official portal before applying. We do not guarantee approval.</p>
          </div>
        </ScrollSection>

        {/* 9. Projects You Can Build */}
        {sec.projectsYouCanBuild?.length > 0 && (
          <ScrollSection id="projects">
            <SectionHeader icon={Lightbulb} title="Projects You Can Build" subtitle="From a 1-week test to a real venture" />
            <div className="grid sm:grid-cols-3 gap-3">
              {sec.projectsYouCanBuild.map((lvl, i) => (
                <div key={i} className="p-5 bg-white border-2 border-gray-100 rounded-2xl">
                  <p className="text-[11px] font-bold text-emerald-700 uppercase tracking-wider mb-2">{lvl.level}</p>
                  <BulletList items={lvl.items} dotColor="bg-emerald-500" />
                </div>
              ))}
            </div>
          </ScrollSection>
        )}

        {/* 10. Career & Industry */}
        {sec.careerAndIndustry && (
          <ScrollSection id="career">
            <SectionHeader icon={Briefcase} title="Career & Industry" subtitle="Where this pathway can lead" />
            {Array.isArray(sec.careerAndIndustry) ? (
              <Pills items={sec.careerAndIndustry} tone="purple" />
            ) : (
              <>
                {sec.careerAndIndustry.shortTerm?.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs font-bold text-gray-700 mb-2">Short term</p>
                    <Pills items={sec.careerAndIndustry.shortTerm} tone="emerald" />
                  </div>
                )}
                {sec.careerAndIndustry.midTerm?.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs font-bold text-gray-700 mb-2">Mid term</p>
                    <Pills items={sec.careerAndIndustry.midTerm} tone="blue" />
                  </div>
                )}
                {sec.careerAndIndustry.longTerm?.length > 0 && (
                  <div>
                    <p className="text-xs font-bold text-gray-700 mb-2">Long term</p>
                    <Pills items={sec.careerAndIndustry.longTerm} tone="purple" />
                  </div>
                )}
              </>
            )}
          </ScrollSection>
        )}

        {/* 11. Future Scope */}
        {sec.futureScope?.length > 0 && (
          <ScrollSection id="future">
            <SectionHeader icon={TrendingUp} title="Future Scope" subtitle="How this can grow over 1–5 years" />
            <Pills items={sec.futureScope} tone="purple" />
            {sec.risks?.length > 0 && (
              <div className="mt-5 p-4 bg-rose-50 border-l-4 border-rose-400 rounded-r-xl">
                <p className="text-xs font-bold text-rose-800 uppercase tracking-wider mb-2">Risks to watch</p>
                <BulletList items={sec.risks} dotColor="bg-rose-500" />
              </div>
            )}
          </ScrollSection>
        )}

        {/* 12. Myths vs Reality */}
        {sec.mythsVsReality?.length > 0 && (
          <ScrollSection id="myths">
            <SectionHeader icon={ShieldCheck} title="Myths vs Reality" subtitle="Get the truth before you start" />
            <div className="space-y-3">
              {sec.mythsVsReality.map((m, i) => (
                <div key={i} className="p-5 bg-white border-2 border-gray-100 rounded-2xl">
                  <div className="flex items-start gap-2.5 mb-2">
                    <span className="inline-flex items-center px-2 py-0.5 bg-rose-100 text-rose-800 text-[10px] font-bold uppercase tracking-wider rounded-full">Myth</span>
                    <p className="text-base text-gray-800 leading-relaxed flex-1">{m.myth}</p>
                  </div>
                  <div className="flex items-start gap-2.5 pl-2 border-l-2 border-emerald-300 ml-1 mt-3">
                    <span className="inline-flex items-center px-2 py-0.5 bg-emerald-100 text-emerald-800 text-[10px] font-bold uppercase tracking-wider rounded-full">Reality</span>
                    <p className="text-base text-emerald-900 leading-relaxed flex-1">{m.reality}</p>
                  </div>
                </div>
              ))}
            </div>
          </ScrollSection>
        )}

        {/* 13. Related Pathways */}
        {related.length > 0 && (
          <ScrollSection id="related">
            <SectionHeader icon={Network} title="Related Pathways" subtitle={`Other ${p.category} businesses to consider`} />
            <div className="grid sm:grid-cols-2 gap-3">
              {related.map(r => (
                <Link key={r.id} to={`/explore/entrepreneurship/${r.id}`} className="group p-4 bg-white border-2 border-gray-100 hover:border-emerald-400 rounded-2xl transition-all flex items-start gap-3">
                  <span className="text-2xl">{r.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-900 leading-tight">{r.title}</p>
                    <p className="text-[11px] text-emerald-700 font-semibold mt-1">{r.startBudget}</p>
                  </div>
                  <ChevronRight size={14} className="text-gray-400 group-hover:translate-x-0.5 transition-transform mt-1" />
                </Link>
              ))}
            </div>
          </ScrollSection>
        )}

        {/* 14. Final Takeaway */}
        <ScrollSection id="takeaway">
          <div className={`p-7 md:p-8 bg-gradient-to-br ${accent.from} ${accent.via} ${accent.to} rounded-3xl text-white`}>
            <Quote className="text-white/80 mb-3" size={28} />
            <p className="text-lg md:text-xl leading-[1.55] font-medium mb-5">
              {sec.finalTakeaway || `${p.title} can be tested with a small budget and turned into a real business once the numbers and customers say yes.`}
            </p>
            <button
              onClick={() => navigate('/explore/entrepreneurship')}
              className="inline-flex items-center gap-1.5 text-sm font-semibold bg-white text-gray-900 hover:bg-gray-100 px-4 py-2 rounded-full transition-colors"
            >
              Explore more pathways <ChevronRight size={14} />
            </button>
          </div>
        </ScrollSection>
      </RolePreviewLayout>

      <Footer />
    </div>
    </PreviewUnlockOverlay>
  );
};

/* ── Helpers ── */
const KV = ({ k, v }) => (
  <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl">
    <p className="text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-1.5">{k}</p>
    <p className="text-base text-gray-900 leading-relaxed">{v}</p>
  </div>
);

const PathwayQuiz = ({ quiz, pathwayName }) => {
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const allAnswered = Object.keys(answers).length === quiz.length;
  const yesCount = Object.values(answers).filter(v => v === 'yes').length;
  const reset = () => { setAnswers({}); setSubmitted(false); };
  const pct = quiz.length > 0 ? yesCount / quiz.length : 0;
  const tier = pct >= 0.7
    ? { label: 'Strong fit', cls: 'bg-emerald-50 border-emerald-300 text-emerald-900' }
    : pct >= 0.4
      ? { label: 'Moderate fit', cls: 'bg-amber-50 border-amber-300 text-amber-900' }
      : { label: 'Explore other pathways first', cls: 'bg-rose-50 border-rose-300 text-rose-900' };

  return (
    <div className="bg-gradient-to-br from-teal-50/50 to-emerald-50/50 border-2 border-teal-200 rounded-2xl p-5 md:p-7" data-testid="pathway-self-check-quiz">
      {!submitted ? (
        <>
          <ol className="space-y-3 mb-5">
            {quiz.map((q, i) => (
              <li key={i} className="p-4 bg-white rounded-xl border border-gray-200">
                <p className="text-base font-medium text-gray-900 mb-3 leading-snug">
                  <span className="text-emerald-700 font-bold mr-1">Q{i + 1}.</span>
                  {q.question}
                </p>
                <div className="flex gap-2 mb-3">
                  {['yes', 'no'].map(opt => (
                    <button
                      key={opt}
                      data-testid={`pathway-q${i}-${opt}`}
                      onClick={() => setAnswers(p => ({ ...p, [i]: opt }))}
                      className={`flex-1 px-3 py-2 text-sm font-semibold rounded-full border-2 transition-all ${
                        answers[i] === opt
                          ? opt === 'yes' ? 'bg-emerald-600 border-emerald-600 text-white' : 'bg-gray-700 border-gray-700 text-white'
                          : 'bg-white border-gray-300 text-gray-700 hover:border-emerald-400'
                      }`}
                    >
                      {opt === 'yes' ? 'Yes' : 'No'}
                    </button>
                  ))}
                </div>
                {answers[i] && (
                  <p className={`text-xs leading-relaxed ${answers[i] === 'yes' ? 'text-emerald-700' : 'text-gray-600'}`}>
                    <span className="font-bold">{answers[i] === 'yes' ? '✓ ' : '→ '}</span>
                    {answers[i] === 'yes' ? q.yesMeans : q.noMeans}
                  </p>
                )}
              </li>
            ))}
          </ol>
          <Button
            data-testid="pathway-quiz-submit"
            disabled={!allAnswered}
            onClick={() => setSubmitted(true)}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40"
          >
            {allAnswered ? 'See my result' : 'Answer all questions to see result'}
          </Button>
        </>
      ) : (
        <div data-testid="pathway-quiz-result">
          <div className={`p-5 rounded-xl border-2 ${tier.cls}`}>
            <p className="text-lg font-bold mb-2 flex items-center gap-2"><CheckCircle2 size={18} /> {tier.label}</p>
            <p className="text-base leading-relaxed">
              You answered <strong>{yesCount} of {quiz.length}</strong> yes-questions positively. Best next step: try the 1-week starter test for {pathwayName}.
            </p>
          </div>
          <button
            onClick={reset}
            data-testid="pathway-quiz-retake"
            className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700 hover:text-emerald-900"
          >
            <RotateCcw size={13} /> Retake quiz
          </button>
        </div>
      )}
    </div>
  );
};

export default EntrepreneurshipDetail;
